<template>
  <div>
    <b-form-group label="Stores" v-if="options && options.length">
      <slot :name="'parentOptions'" :onAddFromParentItem="onAddFromParentItem" :optionParent="optionParent">
        <multiselect
          :placeholder="`Parent ${heading}`"
          :options="options"
          label="partId"
          :allowEmpty="false"
          track-by="_id"
          v-model="optionParent"
          @change="onAddFromParentItem"
        />
      </slot>
    </b-form-group>
    <div class="d-flex justify-content-between align-items-center mb-2">
      <span :class="[headingClass]"
        ><i :class="`far fa-${icon}`" v-if="icon" /> {{ heading }} {{ paginate ? `Page: ${currentPage}` : '' }}</span
      >
      <div>
        <div
          v-if="value.length"
          class="btn btn-link ml-3"
          v-b-tooltip.hover
          :title="collapsed ? 'Expand' : `Collapse`"
          @click="collapsed = !collapsed"
        >
          <i class="far fa-fw" :class="{ 'fa-chevron-down': collapsed, 'fa-chevron-up': !collapsed }"></i>
        </div>
        <div class="btn btn-link px-0" @click="onAddItem"><i class="far fa-plus"></i> Add {{ heading }}</div>
      </div>
    </div>
    <div v-show="!collapsed" class="row">
      <template name="fade" is="transition-group">
        <draggable v-model="draggableItems" handle=".handle">
          <div
            :class="`col-md-${cardSize}`"
            v-for="(item, index) in paginatedArray"
            :key="item._id || item.id || getItemIndex(index)">
            <div class="card mb-2">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="d-flex justify-content-start align-items-center">
                    <h5>{{ heading }}</h5>
                    <slot :name="'header'" :item="item"></slot>
                  </div>
                  <div class="d-flex">
                    <div class="btn btn-link" @click="clonePart(item)" v-if="duplicate" v-b-tooltip.hover :title="`Copy`">
                      <i class="far fa-clone"></i>
                    </div>
                    <div
                      class="btn btn-link ml-3"
                      v-if="value.length > minDeleteSize"
                      v-b-tooltip.hover
                      :title="`Delete`"
                      @click="deleteItem(index)"
                    >
                      <i class="far fa-trash-alt"></i>
                    </div>
                    <div
                      v-if="draggable"
                      class="btn btn-link handle">
                      <i class="fa fa-bars cursor-move text-primary mr-2"></i>
                    </div>
                  </div>
                </div>
                <slot :item="item" :index="getItemIndex(index)"></slot>
              </div>
            </div>
          </div>
        </draggable>
      </template>
    </div>
    <b-modal ref="deleteConfirm" :title="`Delete ${heading}`" @ok="deleteConfirmed" @close="closeDeleteModal">
      <p class="mb-0">Are you sure you want to remove record?</p>
    </b-modal>
    <b-pagination-nav
      v-model="currentPage"
      align="center"
      base-url="#"
      :number-of-pages="totalRows"
      :per-page="perPage"
      @change="scrollToTop"
      v-if="paginate"
    ></b-pagination-nav>
  </div>
</template>
<script>
export default {
  inject: ['$validator'],
  name: 'ArrayCard',
  components: {
    Draggable: () => import('vuedraggable')
  },
  props: {
    cardSize: {
      type: Number,
      default: 12
    },
    minDeleteSize: {
      type: Number,
      default: 1
    },
    icon: {
      type: String
    },
    heading: {
      type: String,
      required: true
    },
    draggable: {
      type: Boolean,
      default: false
    },
    headingClass: {
      type: String,
      default: () => 'h6'
    },
    value: {
      type: Array,
      required: true,
      default: () => []
    },
    defaultItem: {
      type: Object,
      required: true
    },
    paginate: {
      type: Boolean,
      default: false
    },
    perPage: {
      type: Number,
      default: 10
    },
    duplicate: {
      type: Boolean,
      default: false
    },
    options: {
      type: Array
    }
  },
  data() {
    return {
      optionParent: JSON.parse(JSON.stringify(this.defaultItem)),
      collapsed: false,
      indexToDelete: -1,
      currentPage: 1
    };
  },
  computed: {
    draggableItems: {
      get() {
        return this.value;
      },
      set(items) {
        this.$emit('input', items);
      }
    },
    totalRows() {
      return Math.ceil(this.value.length / this.perPage);
    },
    paginatedArray() {
      if (this.paginate) {
        return this.value.slice((this.currentPage - 1) * this.perPage, this.perPage * this.currentPage);
      }
      return this.value;
    },
    headingPluralized() {
      if (this.heading.slice(-1) === 's') {
        return `${this.heading}es`;
      }
      return `${this.heading}s`;
    }
  },
  methods: {
    onAddFromParentItem(selectedItem) {
      this.value.push(Object.assign({ id: Date.now() }, JSON.parse(JSON.stringify(selectedItem))));
      this.optionParent = selectedItem;
    },
    onAddItem() {
      this.value.push(Object.assign({ id: Date.now() }, JSON.parse(JSON.stringify(this.defaultItem))));
      this.$emit('add');
    },
    getItemIndex(index) {
      return this.currentPage === 1 ? index : (this.currentPage - 1) * this.perPage + index;
    },
    deleteItem(index) {
      this.$refs.deleteConfirm.show();
      this.indexToDelete = index;
    },
    deleteConfirmed() {
      const itemIndex = this.getItemIndex(this.indexToDelete);
      this.$emit('delete', this.value[itemIndex]);
      this.value.splice(itemIndex, 1);
      this.currentPage = Math.ceil(this.value.length / this.perPage);
      this.closeDeleteModal();
    },

    closeDeleteModal() {
      this.$refs.deleteConfirm.hide();
      this.indexToDelete = -1;
    },
    scrollToTop(page) {
      console.log(page);
      window.scrollTo(0, 0);
    },
    clonePart(item) {
      try {
        const model = JSON.parse(JSON.stringify(item));
        delete model._id;
        model.id = Date.now();
        this.value.push(model);

        setTimeout(() => (this.currentPage = this.value.length));
      } catch (e) {
        this.notificateError('Clone failed');
      }
    }
  }
};
</script>
